import { useCallback, useState } from "react";
import { Alert, Button, Modal, TextInput, useToast } from "@/ui";
import { api, ApiClientError } from "@/lib/api";
import { copyText } from "@/lib/clipboard";

/**
 * Adds a second (or third) org admin to an organization that already exists.
 *
 * The same person-shape and the same server route the create-organization form
 * uses for the FIRST admin, so the two can never drift: one provisioning path,
 * one temporary password, one welcome message.
 *
 * The new admin gets full org-admin access — there is no lesser tier — so the
 * dialog says that in words rather than leaving it to be discovered.
 */

interface AddedAdmin {
  admin: { id: string; name: string; email: string | null; phone: string | null; displayId: string | null };
  tempPassword: string;
  emailSent: boolean;
}

export function AddOrgAdminModal({
  open,
  orgId,
  orgName,
  onClose,
  onAdded,
}: {
  open: boolean;
  orgId: string;
  orgName: string;
  onClose: () => void;
  /** Called once, after the dialog is dismissed, so the page reloads its list. */
  onAdded: () => void;
}) {
  const toast = useToast();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<AddedAdmin | null>(null);

  const reset = useCallback(() => {
    setName("");
    setEmail("");
    setPhone("");
    setFieldErrors({});
    setResult(null);
  }, []);

  const handleClose = useCallback(() => {
    // The list only needs re-reading if something was actually added.
    const added = result !== null;
    reset();
    onClose();
    if (added) onAdded();
  }, [onAdded, onClose, reset, result]);

  const handleSubmit = useCallback(async () => {
    setSaving(true);
    setFieldErrors({});
    try {
      const res = await api.post<AddedAdmin>(`/platform/orgs/${orgId}/admins`, {
        name: name.trim(),
        // Email is optional throughout (D-18) — a blank field means "no email",
        // and the server rejects an empty string rather than ignoring it.
        ...(email.trim() ? { email: email.trim() } : {}),
        phone: phone.trim(),
      });
      setResult(res);
    } catch (err) {
      if (err instanceof ApiClientError && err.fields) {
        setFieldErrors(err.fields);
      } else {
        toast.error(err instanceof Error ? err.message : "Could not add that admin.");
      }
    } finally {
      setSaving(false);
    }
  }, [email, name, orgId, phone, toast]);

  if (result) {
    const firstName = result.admin.name.split(" ")[0] ?? result.admin.name;
    return (
      <Modal
        footer={<Button onClick={handleClose}>Done</Button>}
        onClose={handleClose}
        open={open}
        title={`${result.admin.name} can now sign in`}
      >
        <div className="flex flex-col gap-4">
          {result.emailSent ? (
            <Alert kind="info" title="Welcome email sent">
              A welcome email with these credentials went to {result.admin.email}.
            </Alert>
          ) : (
            <Alert kind={result.admin.email ? "warning" : "info"} title="Share these credentials">
              {result.admin.email
                ? `The welcome email to ${result.admin.email} did not go out.`
                : `${result.admin.name} signs in with ${result.admin.phone ?? "their mobile number"}.`}
            </Alert>
          )}
          <div className="rounded-md border border-rule bg-surface p-4">
            <p className="text-xs font-semibold tracking-[0.08em] text-muted uppercase">
              Temporary password
            </p>
            <div className="mt-2 flex items-center justify-between gap-3">
              <code className="font-mono text-lg tracking-wide text-ink">{result.tempPassword}</code>
              <Button
                onClick={() => {
                  void copyText(result.tempPassword).then((ok) =>
                    ok
                      ? toast.success("Copied to clipboard.")
                      : toast.error("Could not copy. Select the password and copy it by hand."),
                  );
                }}
                size="sm"
                variant="secondary"
              >
                Copy
              </Button>
            </div>
            <p className="mt-2 text-xs text-muted">
              Shown once. Expires in 7 days or as soon as {firstName} signs in and changes it.
            </p>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      description={`They get the same full access to ${orgName} as its existing admins.`}
      footer={
        <>
          <Button onClick={handleClose} variant="secondary">
            Cancel
          </Button>
          <Button
            disabled={name.trim().length < 2 || phone.trim() === ""}
            loading={saving}
            onClick={() => void handleSubmit()}
          >
            Add admin
          </Button>
        </>
      }
      onClose={handleClose}
      open={open}
      title="Add an org admin"
    >
      <div className="grid grid-cols-1 gap-4">
        <TextInput
          error={fieldErrors["name"]}
          label="Full name"
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Rajesh Sharma"
          value={name}
        />
        <TextInput
          error={fieldErrors["email"]}
          hint="Optional. Without one they sign in with their mobile number."
          label="Work email"
          onChange={(e) => setEmail(e.target.value)}
          placeholder="admin@company.com"
          type="email"
          value={email}
        />
        <TextInput
          error={fieldErrors["phone"]}
          label="Mobile number"
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+91 98765 43210"
          required
          value={phone}
        />
      </div>
    </Modal>
  );
}
